import axios, { AxiosResponse } from 'axios'

import { ResponseSuccess } from '@models/apiBase'
import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { ID } from '@models/base'
import { TaskAssignModeEnum, TaskModel, TaskPriorityEnum, TaskStatusEnum } from '@models/task'

import { API_URL } from '@/constants/constant'
import { TaskAttachmentModel } from '@models/taskAttachment'

const TASKS_URL = `${API_URL}/tasks`

type ResponseTask = Response<{ task: TaskModel }>
type ResponseTasks = Response<WithFilter<Array<TaskModel>>>

type ResponseAttachments = Response<{
  task_attachments: Array<TaskAttachmentModel>
}>

type ResponseAttachment = Response<{
  task_attachment: TaskAttachmentModel
}>

type ResponseTaskTotal = Response<{ total: number }>

export type PayloadCreateUpdateTask = {
  assigned_user_id: ID
  descriptions: string
  due_date?: string
  name: string
  patient_id?: ID
  priority: TaskPriorityEnum
  status: TaskStatusEnum
}

export type PayloadTaskFilter = RequestQuery<{
  priorities: Array<TaskPriorityEnum> | null
  statuses: Array<TaskStatusEnum> | null
  task_assign_mode: TaskAssignModeEnum | null
}>

interface ITasksAPI {
  filter: (query: PayloadTaskFilter) => Promise<ResponseTasks>

  get: (id: ID) => Promise<ResponseTask>

  getTotal: () => Promise<ResponseTaskTotal>

  create: (payload: PayloadCreateUpdateTask) => Promise<ResponseTask>

  update: (id: ID, payload: PayloadCreateUpdateTask) => Promise<ResponseTask>

  delete: (id: ID) => Promise<ResponseSuccess>

  updateStatus: (id: ID, status: TaskStatusEnum) => Promise<ResponseTask>

  updatePriority: (id: ID, priority: TaskPriorityEnum) => Promise<ResponseTask>

  getAttachments: (taskId: ID) => Promise<ResponseAttachments>

  deleteAttachment: (id: ID, taskId: ID) => Promise<ResponseSuccess>

  uploadAttachment: (
    file: File,
    id: ID,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
    controller?: AbortController
  ) => Promise<ResponseAttachment>

  downloadAttachment: (taskId: ID, taskAttachmentId: ID) => Promise<Blob>
}

export const TasksAPI: ITasksAPI = {
  filter: async (query) => {
    return axios.post(`${TASKS_URL}/filter`, query).then((r: AxiosResponse<ResponseTasks>) => r.data)
  },

  get: async (id) => {
    return axios.get(`${TASKS_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  getTotal: async () => {
    return axios.get(`${TASKS_URL}/my-todo`).then((r: AxiosResponse) => r.data)
  },

  create: async (payload) => {
    return axios.post(TASKS_URL, payload).then((r: AxiosResponse) => {
      return r.data
    })
  },

  update: async (id, payload) => {
    return axios.put(`${TASKS_URL}/${id}`, payload).then((r: AxiosResponse) => r.data)
  },

  delete: async (id) => {
    return axios.delete(`${TASKS_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  updateStatus: async (id, status) => {
    return axios.patch(`${TASKS_URL}/${id}/status`, { status }).then((r: AxiosResponse) => r.data)
  },

  updatePriority: async (id, priority) => {
    return axios.patch(`${TASKS_URL}/${id}/priority`, { priority }).then((r: AxiosResponse) => r.data)
  },

  getAttachments: async (taskId) => {
    return axios.get(`${TASKS_URL}/${taskId}/attachments`).then((r: AxiosResponse) => r.data)
  },

  deleteAttachment: async (id, taskId) => {
    return axios.delete(`${TASKS_URL}/${taskId}/attachments/${id}`).then((r: AxiosResponse) => r.data)
  },

  uploadAttachment: async (file, id, onUploadProgress, controller) => {
    var bodyFormData = new FormData()
    bodyFormData.append('file', file)

    return axios
      .post(`${TASKS_URL}/${id}/attachments`, bodyFormData, {
        signal: controller?.signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress,
      })
      .then((r: AxiosResponse) => r.data)
  },

  downloadAttachment: async (taskId, taskAttachmentId) => {
    return axios
      .post(`${TASKS_URL}/${taskId}/attachments/${taskAttachmentId}/download`, undefined, { responseType: 'blob' })
      .then((r) => r.data)
  },
}
